import { useMemo } from 'react';
import {
    ProductYachtDataService,
    YachtFeature,
    EquipmentCategory,
    YachtCondition,
    YachtMonthlyPrice,
    Service,
} from '@/services/ProductYachtDataService';

export interface UseProductYachtDataReturn {
    features: YachtFeature[];
    equipment: EquipmentCategory[];
    conditions: YachtCondition[];
    checkIn: string | null;
    checkOut: string | null;
    includedServices: Service[];
    excludedServices: Service[];
    optionalServices: Service[];
    monthlyPrices: YachtMonthlyPrice[];
    information: {
        content: string | null;
        fieldTitle: string | null;
    };
    marinaInfo: {
        name: string | null;
        location: string | null;
        description: string | null;
    };
    servicesContent: {
        includedContent: string | null;
        excludedContent: string | null;
        includedTitle: string | null;
        excludedTitle: string | null;
    };
    tabFields: Array<{
        tabName: string;
        sectionId: string;
        fields: Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
        }>;
    }>;
}

export function useProductYachtData(
    product: any,
    monthlyPrices?: any[],
    language: string = 'en'
): UseProductYachtDataReturn {
    const productId = product?.id;
    const productMetas = product?.metas;
    const productFields = product?.fields;
    const currency = product?.metas?.currency || product?.currency?.code || 'EUR';

    return useMemo(() => {
        const metas = productMetas || {};
        const fields = productFields || {};

        const features = ProductYachtDataService.parseYachtFeatures(metas, fields);
        const equipment = ProductYachtDataService.parseEquipment(metas, fields, language);
        const conditions = ProductYachtDataService.parseConditions(metas, fields);
        const checkInOut = ProductYachtDataService.parseCheckInOut(metas, fields);
        const services = ProductYachtDataService.parseServices(metas);
        const monthlyPricesData = ProductYachtDataService.parseMonthlyPrices(monthlyPrices || [], currency);
        const information = ProductYachtDataService.parseInformation(metas, fields, language);
        const marinaInfo = ProductYachtDataService.parseMarinaInfo(metas, fields);
        const servicesContent = ProductYachtDataService.parseServicesContent(metas, fields, language);
        const tabFields = ProductYachtDataService.parseTabFields(metas, fields, language);

        return {
            features,
            equipment,
            conditions,
            checkIn: checkInOut.checkIn,
            checkOut: checkInOut.checkOut,
            includedServices: services.included,
            excludedServices: services.excluded,
            optionalServices: services.optional,
            monthlyPrices: monthlyPricesData,
            information,
            marinaInfo,
            servicesContent,
            tabFields,
        };
    }, [productId, productMetas, productFields, monthlyPrices, currency, language]);
}

